import {Dispatch, FC, SetStateAction, useCallback} from 'react';
import {
	formatAccountDisplay,
	formatBikDisplay,
	formatPhoneForReview,
	formatRecipientFullName,
	formatRecipientShortName,
	getStatusConnectionPaymentMethods,
	PaymentAccountNumberStep,
	PaymentBankBikStep,
	PaymentBankSelectStep,
	PaymentCardReviewStep,
	PaymentCardStep,
	PaymentDetailsValues,
	PaymentMethodStep,
	PaymentReviewStep,
	PaymentSbpPhoneStep,
	PaymentSubStep,
	PersonalInfoStepValues,
	RegistrationPaymentMethod,
	RegistrationWorkStatus,
	SelectedBank,
} from 'features/registration';

type Props = {
	workStatus: RegistrationWorkStatus | null;
	paymentMethods: RegistrationPaymentMethod[];
	pendingPaymentMethod: RegistrationPaymentMethod | null;
	paymentSubStep: PaymentSubStep;
	paymentDetails: PaymentDetailsValues;
	personalInfo: PersonalInfoStepValues;
	hasExistingPaymentWays: boolean;
	onPaymentMethodSelect: (method: RegistrationPaymentMethod) => void;
	onPatchPaymentDetails: (patch: Partial<PaymentDetailsValues>) => void;
	onCardBankResolved: (selectedBank: SelectedBank | null) => void;
};

/** Экран выбора способа выплат либо подшаг ввода реквизитов. */
export const PaymentMethodStepSwitch: FC<Props> = ({
	workStatus,
	paymentMethods,
	pendingPaymentMethod,
	paymentSubStep,
	paymentDetails,
	personalInfo,
	hasExistingPaymentWays,
	onPaymentMethodSelect,
	onPatchPaymentDetails,
	onCardBankResolved,
}) => {
	const statusConnectionPaymentMethods = getStatusConnectionPaymentMethods(workStatus, paymentMethods);

	if (!pendingPaymentMethod || paymentSubStep === 'select') {
		return (
			<PaymentMethodStep
				onChange={onPaymentMethodSelect}
				allowedMethods={statusConnectionPaymentMethods}
			/>
		);
	}

	switch (paymentSubStep) {
		case 'sbp_phone':
			return (
				<PaymentSbpPhoneStep
					value={paymentDetails.phone}
					onChange={(phone) => onPatchPaymentDetails({phone})}
				/>
			);
		case 'sbp_bank':
			return (
				<PaymentBankSelectStep
					selectedBank={paymentDetails.selectedBank}
					onSelect={(selectedBank) => onPatchPaymentDetails({selectedBank})}
				/>
			);
		case 'sbp_review':
			return (
				<PaymentReviewStep
					fields={[
						{label: 'Получатель', value: formatRecipientShortName(personalInfo)},
						{label: 'Номер телефона', value: formatPhoneForReview(paymentDetails.phone)},
					]}
					bank={paymentDetails.selectedBank}
					isPrimary={paymentDetails.isPrimary}
					showPrimaryToggle={hasExistingPaymentWays}
					onPrimaryChange={(isPrimary) => onPatchPaymentDetails({isPrimary})}
				/>
			);
		case 'card_number':
			return (
				<PaymentCardStep
					value={paymentDetails.card}
					onChange={(card) => onPatchPaymentDetails({card, selectedBank: null})}
				/>
			);
		case 'card_review':
			return (
				<PaymentCardReviewStep
					card={paymentDetails.card}
					isPrimary={paymentDetails.isPrimary}
					showPrimaryToggle={hasExistingPaymentWays}
					onPrimaryChange={(isPrimary) => onPatchPaymentDetails({isPrimary})}
					onBankResolved={onCardBankResolved}
				/>
			);
		case 'account_bik':
			return (
				<PaymentBankBikStep
					value={paymentDetails.bik}
					selectedBank={paymentDetails.selectedBank}
					onChange={(bik) => onPatchPaymentDetails({bik})}
					onBankSelect={(selectedBank) => onPatchPaymentDetails({selectedBank})}
				/>
			);
		case 'account_number':
			return (
				<PaymentAccountNumberStep
					value={paymentDetails.account}
					workStatus={workStatus}
					error={paymentDetails.accountError}
					onChange={(account) => onPatchPaymentDetails({account})}
					onClearError={() => onPatchPaymentDetails({accountError: null})}
				/>
			);
		case 'account_review':
			return (
				<PaymentReviewStep
					fields={[
						{label: 'Получатель', value: formatRecipientFullName(personalInfo)},
						{label: 'БИК банка', value: formatBikDisplay(paymentDetails.bik)},
						{label: 'Номер счёта', value: formatAccountDisplay(paymentDetails.account)},
					]}
					bank={paymentDetails.selectedBank}
					isPrimary={paymentDetails.isPrimary}
					showPrimaryToggle={hasExistingPaymentWays}
					onPrimaryChange={(isPrimary) => onPatchPaymentDetails({isPrimary})}
				/>
			);
		default:
			return (
				<PaymentMethodStep
					onChange={onPaymentMethodSelect}
					allowedMethods={statusConnectionPaymentMethods}
				/>
			);
	}
};

export const useCardBankResolvedHandler = (
	setPaymentDetails: Dispatch<SetStateAction<PaymentDetailsValues>>,
) => {
	return useCallback(
		(selectedBank: SelectedBank | null) => {
			setPaymentDetails((current) =>
				current.selectedBank?.id === selectedBank?.id ? current : {...current, selectedBank},
			);
		},
		[setPaymentDetails],
	);
};
